import { create } from 'zustand';
import type { MetadataChangeItem } from '../lib/bundleDiff';

/** Bir ekran için bekleyen ya da yeni uygulanmış metadata değişikliği. */
interface ScreenWatchState {
  /** Sunucudan sinyal geldi, kullanıcı henüz uygulamadı. */
  pending: boolean;
  /** Uygulandıktan sonra gösterilecek fark; şerit ve kolon vurgusu bunu kullanır. */
  changes: MetadataChangeItem[];
  /** Vurgulanacak kolon adları — süresi dolunca temizlenir. */
  highlights: string[];
}

interface MetadataWatchState {
  /** screenId (küçük harf) → durum. */
  screens: Record<string, ScreenWatchState>;
  /**
   * Düzenleme süren ekranlar: screenId → aynı anda açık düzenleme sayısı.
   *
   * Sayaç, bayrak değil: bir ekranda satır içi düzenleme ile açık bir edit form aynı
   * anda olabilir ve biri kapanınca kilit erken düşerdi.
   */
  editLocks: Record<string, number>;
  lockEdit: (screenId: string) => void;
  unlockEdit: (screenId: string) => void;
  markPending: (screenIds: string[]) => void;
  clearPending: (screenId: string) => void;
  setApplied: (screenId: string, changes: MetadataChangeItem[], highlights: string[]) => void;
  /** Yalnızca kolon vurgusunu söndürür; bekleyen uyarıya dokunmaz. */
  clearHighlights: (screenId: string) => void;
  dismiss: (screenId: string) => void;
}

const EMPTY: ScreenWatchState = { pending: false, changes: [], highlights: [] };

export const useMetadataWatchStore = create<MetadataWatchState>((set) => ({
  screens: {},
  editLocks: {},

  lockEdit: (screenId) =>
    set((state) => {
      const key = screenId.toLowerCase();
      return { editLocks: { ...state.editLocks, [key]: (state.editLocks[key] ?? 0) + 1 } };
    }),

  unlockEdit: (screenId) =>
    set((state) => {
      const key = screenId.toLowerCase();
      const next = (state.editLocks[key] ?? 0) - 1;
      const editLocks = { ...state.editLocks };
      if (next > 0) editLocks[key] = next;
      else delete editLocks[key];
      return { editLocks };
    }),

  markPending: (screenIds) =>
    set((state) => {
      if (screenIds.length === 0) return state;
      const screens = { ...state.screens };
      for (const id of screenIds) {
        const key = id.toLowerCase();
        screens[key] = { ...(screens[key] ?? EMPTY), pending: true };
      }
      return { screens };
    }),

  clearPending: (screenId) =>
    set((state) => {
      const key = screenId.toLowerCase();
      if (!state.screens[key]?.pending) return state;
      return { screens: { ...state.screens, [key]: { ...state.screens[key], pending: false } } };
    }),

  setApplied: (screenId, changes, highlights) =>
    set((state) => ({
      screens: { ...state.screens, [screenId.toLowerCase()]: { pending: false, changes, highlights } },
    })),

  clearHighlights: (screenId) =>
    set((state) => {
      const key = screenId.toLowerCase();
      const current = state.screens[key];
      if (!current?.highlights.length) return state;
      return { screens: { ...state.screens, [key]: { ...current, highlights: [] } } };
    }),

  dismiss: (screenId) =>
    set((state) => {
      const key = screenId.toLowerCase();
      if (!state.screens[key]) return state;
      const screens = { ...state.screens };
      delete screens[key];
      return { screens };
    }),
}));

/** Ekranda düzenleme sürüyor mu? Otomatik tazeleme buna bakar. */
export function isEditLocked(screenId: string): boolean {
  return (useMetadataWatchStore.getState().editLocks[screenId.toLowerCase()] ?? 0) > 0;
}

/** Bileşenlerin tek satırda kullanabilmesi için; seçici her render'da yeni nesne üretmez. */
export function useScreenWatch(screenId: string | null | undefined): ScreenWatchState {
  return useMetadataWatchStore((s) => (screenId ? (s.screens[screenId.toLowerCase()] ?? EMPTY) : EMPTY));
}
